/* =====================================================================
 * LIVE AGAIN, 2026-09-20 — and this is the use it was kept for.
 *
 * It was inert from 2026-08-22, when the fixed CandidateApplicationForm
 * replaced the dynamic question engine as step one of a sitting. The
 * file was kept rather than deleted on the argument that the pool had a
 * real future use: "a second, exam-specific question set alongside the
 * application". The video screening round is that set.
 *
 * WHAT MUST NOT COME BACK WITH IT: `complete_intro` no longer gates on
 * the sealed question snapshot, and it had to stop — a candidate cannot
 * answer a question nothing renders, and the gate was a permanent dead
 * end for any attempt sealed against an exam with questions selected.
 * The video round gates itself, question by question, and never goes
 * through that path. See CLAUDE.md.
 * ===================================================================== */

import * as Dialog from "@radix-ui/react-dialog";
import { Archive, ArchiveRestore, Pencil, Plus, X } from "lucide-react";
import { useState } from "react";

import { useToast } from "@/hooks/use-toast";

import { Pagination } from "./Pagination";
import { pageSizeFor } from "@/lib/paging";
import type { TestQuestion } from "@/api/tests-api";
import {
  useCreateQuestion,
  usePatchQuestion,
  useQuestions,
} from "@/api/tests-api";
import type { Round } from "@/lib/questions";

/**
 * The pool of introduction questions.
 *
 * Step one of an assessment is a short Q&A before any case is opened.
 * The exam builder could already PICK from this pool, but nothing could
 * put anything in it — the questions existed only if someone created
 * them over the API. This is that missing half.
 *
 * A question is never deleted, only retired. An exam stores the ids it
 * selected, and an attempt freezes the questions it actually asked, so a
 * hard delete would strand both. Retiring drops it out of the picker and
 * leaves every past sitting readable.
 */

const KINDS: { value: TestQuestion["kind"]; label: string; hint: string }[] = [
  { value: "TEXT", label: "Short text", hint: "One line." },
  { value: "LONG_TEXT", label: "Paragraph", hint: "A few sentences." },
  { value: "EMAIL", label: "Email", hint: "Validated as an address." },
  { value: "DATE", label: "Date", hint: "A date picker." },
  { value: "NUMBER", label: "Number", hint: "With an optional range." },
  { value: "SINGLE_CHOICE", label: "Pick one", hint: "One option from a list." },
  { value: "MULTI_CHOICE", label: "Pick any", hint: "Any number of options." },
  {
    value: "SCALE",
    label: "Rating grid",
    hint: "Rate several rows on one shared scale.",
  },
  {
    value: "NUMBER_GRID",
    label: "Number grid",
    hint: "A number per row, totalled.",
  },
];

/**
 * A video round has exactly one kind of question, so it is not offered
 * as a choice.
 *
 * Every answer in that round is spoken to camera — that is the whole
 * point of it, and the reason there is no retake and no going back. A
 * kind picker there would offer an admin combinations that cannot work,
 * and the mistake would surface as a candidate meeting a text box on a
 * camera screen, mid-sitting, in the one attempt they get.
 */
const VIDEO_KIND: TestQuestion["kind"] = "VIDEO";

/** Defaults for a new spoken question: round 2's commonest pair --
 *  45 seconds to think, a minute and a half to answer. */
const DEFAULT_THINK = "45";
const DEFAULT_ANSWER = "90";
/** The "time's up" beat the server adds after the answer time. */
const GRACE_SECONDS = 5;


/** mm:ss, for a number the admin typed in seconds. */
function asClock(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

/** Kinds that take a list of options. */
const CHOICE_KINDS = new Set(["SINGLE_CHOICE", "MULTI_CHOICE"]);
/** Kinds whose rows live in config_json. */
const GRID_KINDS = new Set(["SCALE", "NUMBER_GRID"]);

/**
 * The rows of a SCALE or NUMBER_GRID, edited as text.
 *
 * One row per line, `id | label | hint`. A textarea rather than a
 * repeater of input groups because these lists get written in one go and
 * pasted between exams -- six modalities, five systems -- and a five-row
 * repeater takes fifteen clicks to fill and one to lose.
 *
 * THE ID IS WHAT THE ANSWER IS STORED AGAINST. Rename a label freely;
 * change an id and every answer already given against it is orphaned,
 * silently. Said again in the hint beside the field.
 */
function parseRows(text: string) {
  return text
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [id, label, hint] = line.split("|").map((s) => s.trim());
      return {
        id: id ?? "",
        label: label || id || "",
        ...(hint ? { hint } : {}),
      };
    })
    .filter((r) => r.id);
}

const rowsToText = (rows: { id: string; label: string; hint?: string }[]) =>
  rows
    .map((r) => [r.id, r.label, r.hint].filter(Boolean).join(" | "))
    .join("\n");

export function QuestionLibraryPanel() {
  const { toast } = useToast();
  const [showRetired, setShowRetired] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(pageSizeFor("questions"));
  // WHICH POOL is on screen. The two rounds ask completely different
  // things -- one is a written application, the other is spoken to
  // camera -- so listing them together would only ever be a way to pick
  // the wrong one.
  const [round] = useState<Round>("VIDEO_ROUND");
  const questions = useQuestions(showRetired, page, pageSize, round);
  const create = useCreateQuestion();
  const patch = usePatchQuestion();

  // One dialog for both jobs, as on the Exams page: adding and editing
  // are the same fields, and splitting them is how two behaviours end up
  // where there should be one. `editing` null while open means "new".
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<TestQuestion | null>(null);

  const [prompt, setPrompt] = useState("");
  const [kind, setKind] = useState<TestQuestion["kind"]>("TEXT");
  const [optionsText, setOptionsText] = useState("");
  // NO CONTROL DRAWS THIS ANY MORE -- see the note where the checkbox
  // used to be. It is still held and still posted so that editing a row
  // round-trips whatever it already carried, rather than silently
  // rewriting it to false on the first save.
  const [required, setRequired] = useState(false);
  const [sectionCode, setSectionCode] = useState("");
  const [sectionTitle, setSectionTitle] = useState("");
  const [sectionHint, setSectionHint] = useState("");
  const [helpText, setHelpText] = useState("");
  const [placeholder, setPlaceholder] = useState("");
  const [rowsText, setRowsText] = useState("");
  const [scaleMin, setScaleMin] = useState("1");
  const [scaleMax, setScaleMax] = useState("5");
  const [scaleKey, setScaleKey] = useState("");
  const [gridUnit, setGridUnit] = useState("");
  // The round the DIALOG is writing to, which starts as the one being
  // browsed: what you create lands where you are looking.
  const [formRound, setFormRound] = useState<Round>("VIDEO_ROUND");
  // ROUND 2's shape: a title, thinking time, answer time, and whether
  // it is the unscored warm-up.
  const [title, setTitle] = useState("");
  const [think, setThink] = useState(DEFAULT_THINK);
  const [answer, setAnswer] = useState(DEFAULT_ANSWER);
  const [warmup, setWarmup] = useState(false);

  const isVideo = formRound === "VIDEO_ROUND";
  const thinkSeconds = Number(think);
  const answerSeconds = Number(answer);
  // Mirrors the server's bounds so the reason appears while typing
  // rather than as a 422 after pressing Save.
  const thinkValid =
    Number.isInteger(thinkSeconds) && thinkSeconds >= 0 && thinkSeconds <= 600;
  const answerValid =
    Number.isInteger(answerSeconds) && answerSeconds >= 15 && answerSeconds <= 900;
  const allottedValid = thinkValid && answerValid && title.trim().length <= 160;

  // Was ``kind !== "TEXT"``, which was right when TEXT was the only kind
  // without options. With nine kinds it demanded two options for an
  // EMAIL, a DATE and a NUMBER -- so Save stayed disabled for ever with
  // nothing on screen explaining why.
  const needsOptions = !isVideo && CHOICE_KINDS.has(kind);
  const needsRows = !isVideo && GRID_KINDS.has(kind);
  const gridRows = needsRows ? parseRows(rowsText) : [];
  const options = optionsText
    .split("\n")
    .map((o) => o.trim())
    .filter(Boolean);
  const canSave =
    prompt.trim().length > 0 &&
    (!needsOptions || options.length >= 2) &&
    (!needsRows || gridRows.length >= 1) &&
    (!isVideo || allottedValid) &&
    !create.isPending &&
    !patch.isPending;

  const rows = questions.data?.items ?? [];
  const total = questions.data?.total ?? 0;

  const close = () => {
    setOpen(false);
    setEditing(null);
    setPrompt("");
    setOptionsText("");
    setKind("TEXT");
    setRequired(false);
    setSectionCode("");
    setSectionTitle("");
    setSectionHint("");
    setHelpText("");
    setPlaceholder("");
    setRowsText("");
    setScaleMin("1");
    setScaleMax("5");
    setScaleKey("");
    setGridUnit("");
    setTitle("");
    setThink(DEFAULT_THINK);
    setAnswer(DEFAULT_ANSWER);
    setWarmup(false);
    setFormRound(round);
  };

  const openNew = () => {
    close();
    setFormRound(round);
    setOpen(true);
  };

  const openEdit = (q: TestQuestion) => {
    setEditing(q);
    setPrompt(q.prompt);
    setKind(q.kind);
    setOptionsText((q.options_json ?? []).join("\n"));
    setRequired(q.required);
    setSectionCode(q.section_code ?? "");
    setSectionTitle(q.section_title ?? "");
    setSectionHint(q.section_hint ?? "");
    setHelpText(q.help_text ?? "");
    setPlaceholder(q.placeholder ?? "");
    const cfg = q.config_json ?? {};
    setRowsText(rowsToText(cfg.rows ?? []));
    setScaleMin(String(cfg.min ?? 1));
    setScaleMax(String(cfg.max ?? 5));
    setScaleKey(cfg.key ?? "");
    setGridUnit(cfg.unit ?? "");
    // A legacy row carries no round; treat it as what this screen now
    // writes rather than silently moving it back to a pool that renders
    // nowhere.
    setFormRound(q.round ?? "VIDEO_ROUND");
    // A question from before round 2 had ONE clock; it opens as no
    // thinking time and that clock as the answer, and saving it gives it
    // round 2's shape.
    setTitle(cfg.title ?? "");
    setThink(String(cfg.answer_seconds != null ? (cfg.think_seconds ?? 0) : 0));
    setAnswer(String(cfg.answer_seconds ?? cfg.allotted_seconds ?? DEFAULT_ANSWER));
    setWarmup(!!cfg.warmup);
    setOpen(true);
  };

  const buildConfig = () => {
    if (isVideo) {
      return {
        title: title.trim(),
        think_seconds: thinkSeconds,
        answer_seconds: answerSeconds,
        warmup,
      };
    }
    if (kind === "SCALE") {
      return {
        min: Number(scaleMin) || 1,
        max: Number(scaleMax) || 5,
        ...(scaleKey.trim() ? { key: scaleKey.trim() } : {}),
        rows: gridRows,
      };
    }
    if (kind === "NUMBER_GRID") {
      return {
        ...(gridUnit.trim() ? { unit: gridUnit.trim() } : {}),
        min: 0,
        total: true,
        rows: gridRows,
      };
    }
    return {};
  };

  // An emptied input gives "", which means CLEAR IT -- a real
  // instruction, and a different one from "leave it alone". The API
  // distinguishes them by whether the key is sent at all, so the
  // conversion happens here rather than being guessed there.
  const orNull = (v: string) => (v.trim() ? v.trim() : null);

  const save = async () => {
    if (!canSave) return;
    try {
      if (editing) {
        await patch.mutateAsync({
          questionId: editing.id,
          body: {
            prompt: prompt.trim(),
            // The KIND is deliberately not sent on an edit: an attempt
            // froze this question's kind with its snapshot, and turning a
            // free-text question into a multiple choice would leave past
            // answers unreadable against the thing they answered.
            options_json: needsOptions ? options : [],
            required,
            // The round CAN be corrected, unlike the kind above: moving a
            // question between pools changes which exams may draw it,
            // never how an answer already given is read.
            round: formRound,
            section_code: orNull(sectionCode),
            section_title: orNull(sectionTitle),
            section_hint: orNull(sectionHint),
            help_text: orNull(helpText),
            placeholder: orNull(placeholder),
            config_json: buildConfig(),
          },
        });
        toast({ title: "Question saved" });
      } else {
        await create.mutateAsync({
          prompt: prompt.trim(),
          // Forced rather than chosen in a video round: see VIDEO_KIND.
          kind: isVideo ? VIDEO_KIND : kind,
          round: formRound,
          options_json: needsOptions ? options : [],
          required,
          section_code: orNull(sectionCode),
          section_title: orNull(sectionTitle),
          section_hint: orNull(sectionHint),
          help_text: orNull(helpText),
          placeholder: orNull(placeholder),
          config_json: buildConfig(),
        });
        toast({ title: "Question added" });
      }
      close();
    } catch (err) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? "Could not save the question";
      toast({ title: detail, variant: "error" });
    }
  };

  return (
    <div data-cy="question-library" className="flex flex-col gap-8">
      <section>
        {/* THERE IS NO ROUND TO CHOOSE, and the tabs that used to be
            here were a way to do harmless-looking damage.

            Round 1's step one is the FIXED candidate application --
            nine sections, thirty-five required answers, its own table.
            It has been that since 2026-08-22 and stays that way. The
            dynamic question engine renders nowhere in it, so a question
            written against Round 1 is saved, listed, pickable in the
            exam builder, sealed onto a candidate's paper, and never
            shown to anybody. Nothing errors; it simply does not appear.

            So this pool is the VIDEO ROUND's, and only that. The legacy
            Round 1 rows are untouched -- they are frozen into seals on
            attempts already sat, and `snapshot_questions` still reads
            them by id -- they are just not editable from a screen whose
            every other row will be asked out loud. */}
        <p
          data-cy="questions-scope-note"
          className="mb-3 text-xs text-foreground-muted"
        >
          These are the questions a candidate answers{" "}
          <span className="font-semibold text-foreground">to camera</span> in a
          video screening round. Round 1 does not use them — its step one is
          the fixed application form, which is not editable here.
        </p>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-sm font-semibold text-foreground">
            Video round questions ({total})
          </h3>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-xs text-foreground-muted">
              <input
                type="checkbox"
                data-cy="toggle-retired-questions"
                checked={showRetired}
                onChange={(e) => {
                  setPage(1);
                  setShowRetired(e.target.checked);
                }}
                className="h-4 w-4 accent-accent"
              />
              Show retired
            </label>
            <button
              type="button"
              data-cy="btn-new-question"
              onClick={openNew}
              className="sk-btn sk-btn-primary sk-sm"
            >
              <Plus aria-hidden className="h-3.5 w-3.5" />
              Question
            </button>
          </div>
        </div>
        {/* The two pools are different enough that one sentence cannot
            describe both: one is written with the clock stopped, the
            other is spoken against a clock per question. */}
        <p className="mb-3 text-xs text-foreground-muted">
          {round === "VIDEO_ROUND"
            ? "Answered on camera, one at a time, each against its own clock. A candidate cannot go back to an earlier question."
            : "Asked before the cases, with the clock stopped. Each exam chooses which of these to include."}
        </p>

        {questions.isLoading ? (
          <p className="text-sm text-foreground-muted">Loading…</p>
        ) : rows.length === 0 ? (
          <p data-cy="no-questions" className="text-sm text-foreground-muted">
            No questions yet — press “+ Question” to add one.
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {rows.map((q) => (
              <QuestionRow key={q.id} question={q} onEdit={openEdit} />
            ))}
          </ul>
        )}

        <Pagination
          page={page}
          pageSize={pageSize}
          total={total}
          onPage={setPage}
          onPageSize={setPageSize}
          label="questions"
        />
      </section>

      {open && (
        <Dialog.Root
          open
          onOpenChange={(next) => {
            if (!next) close();
          }}
        >
          <Dialog.Portal>
            <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50" />
            <Dialog.Content
              data-cy={
                editing ? "edit-question-modal" : "create-question-modal"
              }
              className="fixed left-1/2 top-1/2 z-50 max-h-[90vh] w-full max-w-2xl -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-xl border border-border bg-background-card shadow-theme-lg"
            >
              <div className="flex items-start justify-between gap-3 border-b border-border px-6 pt-6 pb-4">
                <div>
                  <Dialog.Title className="text-lg font-semibold text-foreground-heading">
                    {editing ? "Edit question" : "New question"}
                  </Dialog.Title>
                  <Dialog.Description className="mt-1 text-xs text-foreground-muted">
                    {editing
                      ? "Candidates who have already been asked this keep the wording they saw."
                      : "Added to the pool. Each exam chooses which questions it asks."}
                  </Dialog.Description>
                </div>
                <Dialog.Close
                  data-cy="btn-close-question-form"
                  aria-label="Close"
                  className="rounded-md p-1 text-foreground-muted hover:bg-table-row-hover hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                >
                  <X className="h-4 w-4" />
                </Dialog.Close>
              </div>

              <div className="px-6 py-5">
                <label className="sk-field flex flex-col">
                  <span className="sk-label">Question</span>
                  <textarea
                    data-cy="question-prompt"
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    rows={2}
                    placeholder="How many years have you been reporting independently?"
                    className="sk-input mt-1 w-auto"
                  />
                </label>

                {/* NO ROUND PICKER. A question written against Round 1
                    would be saved, listed, pickable and sealed, and then
                    shown to nobody -- its step one is the fixed
                    application form. Offering the choice was offering
                    one wrong answer. */}

                {isVideo && (
                  <>
                    <label className="sk-field mt-4 flex flex-col">
                      <span className="sk-label">Title</span>
                      <input
                        data-cy="question-title"
                        value={title}
                        maxLength={160}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="The complaint"
                        className="sk-input mt-1 w-auto"
                      />
                      <span className="mt-1 text-xs text-foreground-muted">
                        The heading the candidate sees above the question.
                      </span>
                    </label>
                    <div className="mt-4 flex flex-wrap gap-6">
                      <label className="sk-field flex flex-col">
                        <span className="sk-label">Thinking time</span>
                        <div className="mt-1 flex items-center gap-2">
                          <input
                            data-cy="question-think"
                            type="number"
                            min={0}
                            max={600}
                            step={5}
                            value={think}
                            onChange={(e) => setThink(e.target.value)}
                            className="sk-input w-28"
                          />
                          <span className="text-xs text-foreground-muted">
                            seconds{thinkValid && ` — ${asClock(thinkSeconds)}`}
                          </span>
                        </div>
                      </label>
                      <label className="sk-field flex flex-col">
                        <span className="sk-label">Answer time</span>
                        <div className="mt-1 flex items-center gap-2">
                          <input
                            data-cy="question-answer"
                            type="number"
                            min={15}
                            max={900}
                            step={5}
                            value={answer}
                            onChange={(e) => setAnswer(e.target.value)}
                            className="sk-input w-28"
                          />
                          <span className="text-xs text-foreground-muted">
                            seconds{answerValid && ` — ${asClock(answerSeconds)}`}
                          </span>
                        </div>
                      </label>
                    </div>
                    <span className="mt-1 block text-xs text-foreground-muted">
                      {allottedValid
                        ? `The candidate thinks, then answers; "I'm ready to answer" ends thinking early. A ${GRACE_SECONDS}-second "time's up" follows, then the next question rolls in. ${asClock(thinkSeconds + answerSeconds + GRACE_SECONDS)} in all.`
                        : "Thinking 0 to 10 minutes; answering 15 seconds to 15 minutes."}
                    </span>
                    <label className="sk-check mt-4 flex items-start gap-2.5">
                      <input
                        data-cy="question-warmup"
                        type="checkbox"
                        checked={warmup}
                        onChange={(e) => setWarmup(e.target.checked)}
                      />
                      <span className="text-sm text-foreground">
                        Warm-up — asked first, not scored, and left out of
                        the candidate&rsquo;s question count.
                      </span>
                    </label>
                  </>
                )}

                {/* The kind picker has nothing to offer in a video
                    round: every answer there is spoken. */}
                <fieldset
                  className={isVideo ? "hidden" : "mt-4"}
                  disabled={!!editing}
                >
                  <legend className="text-xs text-foreground-muted">
                    Answer type
                    {editing && " — fixed once the question exists"}
                  </legend>
                  <div className="mt-1 flex flex-wrap gap-4">
                    {KINDS.map((k) => (
                      <label
                        key={k.value}
                        className={[
                          "flex items-start gap-2 text-sm",
                          editing ? "text-foreground-muted" : "text-foreground",
                        ].join(" ")}
                      >
                        <input
                          type="radio"
                          name="question-kind"
                          data-cy="question-kind"
                          value={k.value}
                          checked={kind === k.value}
                          onChange={() => setKind(k.value)}
                          className="mt-1 h-4 w-4 accent-accent"
                        />
                        <span>
                          {k.label}
                          <span className="block text-[11px] text-foreground-muted">
                            {k.hint}
                          </span>
                        </span>
                      </label>
                    ))}
                  </div>
                </fieldset>

                {needsOptions && (
                  <label className="sk-field mt-4 flex flex-col">
                    <span className="sk-label">Options — one per line</span>
                    <textarea
                      data-cy="question-options"
                      value={optionsText}
                      onChange={(e) => setOptionsText(e.target.value)}
                      rows={4}
                      placeholder={
                        "Body / abdominal\nMSK\nNeuro\nBreast\nGeneral"
                      }
                      className="sk-input mt-1 w-auto"
                    />
                    <span className="mt-1 text-[11px] text-foreground-muted">
                      {options.length < 2
                        ? "At least two options."
                        : `${options.length} options.`}
                    </span>
                  </label>
                )}

                {needsRows && (
                  <div className="mt-4 rounded-lg border border-border p-3">
                    <label className="flex flex-col text-xs text-foreground-muted">
                      Rows — one per line, <code>id | label | hint</code>
                      <textarea
                        data-cy="question-rows"
                        value={rowsText}
                        onChange={(e) => setRowsText(e.target.value)}
                        rows={5}
                        placeholder={
                          kind === "SCALE"
                            ? "vr | Voice recognition reporting | Including editing your own text"
                            : "ct | CT"
                        }
                        className="sk-input sk-sm mt-1 font-mono"
                      />
                      <span className="mt-1 text-[11px] text-foreground-muted">
                        {gridRows.length === 0
                          ? "At least one row."
                          : `${gridRows.length} rows.`}{" "}
                        The <strong>id</strong> is what each answer is stored
                        against — rename a label freely, but changing an id
                        orphans every answer already given against it.
                      </span>
                    </label>

                    {kind === "SCALE" ? (
                      <div className="mt-3 grid grid-cols-2 gap-3">
                        <label className="sk-field flex flex-col">
                          <span className="sk-label">Lowest</span>
                          <input
                            type="number"
                            data-cy="scale-min"
                            value={scaleMin}
                            onChange={(e) => setScaleMin(e.target.value)}
                            className="sk-input mt-1"
                          />
                        </label>
                        <label className="sk-field flex flex-col">
                          <span className="sk-label">Highest</span>
                          <input
                            type="number"
                            data-cy="scale-max"
                            value={scaleMax}
                            onChange={(e) => setScaleMax(e.target.value)}
                            className="sk-input mt-1"
                          />
                        </label>
                        <label className="col-span-2 flex flex-col text-xs text-foreground-muted">
                          Scale key — shown above the rows
                          <input
                            data-cy="scale-key"
                            value={scaleKey}
                            onChange={(e) => setScaleKey(e.target.value)}
                            placeholder="1 never used · 5 fully fluent, could teach it"
                            className="sk-input mt-1"
                          />
                          <span className="mt-1 text-[11px] text-foreground-muted">
                            Without this, a 1–5 rating means whatever each
                            candidate assumes it means, and the answers stop
                            being comparable.
                          </span>
                        </label>
                      </div>
                    ) : (
                      <label className="sk-field mt-3 flex flex-col">
                        <span className="sk-label">Unit — shown beside each box</span>
                        <input
                          data-cy="grid-unit"
                          value={gridUnit}
                          onChange={(e) => setGridUnit(e.target.value)}
                          placeholder="/day"
                          className="sk-input mt-1"
                        />
                      </label>
                    )}
                  </div>
                )}

                <label className="sk-field mt-4 flex flex-col">
                  <span className="sk-label">Help text — shown under the question</span>
                  <textarea
                    data-cy="question-help"
                    value={helpText}
                    onChange={(e) => setHelpText(e.target.value)}
                    rows={2}
                    placeholder="Give your sustainable number, not your best-ever day."
                    className="sk-input mt-1"
                  />
                  <span className="mt-1 text-[11px] text-foreground-muted">
                    Often changes what the answer MEANS. Worth writing.
                  </span>
                </label>

                {!isVideo && !needsOptions && !needsRows && (
                  <label className="sk-field mt-4 flex flex-col">
                    <span className="sk-label">Placeholder — greyed example inside the box</span>
                    <input
                      data-cy="question-placeholder"
                      value={placeholder}
                      onChange={(e) => setPlaceholder(e.target.value)}
                      placeholder="e.g. PowerScribe, Nuance, none"
                      className="sk-input mt-1"
                    />
                  </label>
                )}

                {/* Sectioning. Carried on the question rather than in a
                    table of its own, so the seal freezes the heading the
                    candidate actually saw — rename a section tomorrow and
                    a paper sealed today still reads correctly. The cost
                    is that renaming means editing each question in the
                    section, which is why the code and title are plain
                    fields here rather than a picker pretending otherwise. */}
                <fieldset className="mt-4 rounded-lg border border-border p-3">
                  <legend className="px-1 text-xs text-foreground-muted">
                    Section (optional)
                  </legend>
                  <div className="grid grid-cols-[5rem_1fr] gap-3">
                    <label className="sk-field flex flex-col">
                      <span className="sk-label">Code</span>
                      <input
                        data-cy="question-section-code"
                        value={sectionCode}
                        onChange={(e) => setSectionCode(e.target.value)}
                        placeholder="01"
                        className="sk-input mt-1"
                      />
                    </label>
                    <label className="sk-field flex flex-col">
                      <span className="sk-label">Title</span>
                      <input
                        data-cy="question-section-title"
                        value={sectionTitle}
                        onChange={(e) => setSectionTitle(e.target.value)}
                        placeholder="Registration & eligibility"
                        className="sk-input mt-1"
                      />
                    </label>
                  </div>
                  <label className="sk-field mt-3 flex flex-col">
                    <span className="sk-label">Section hint</span>
                    <textarea
                      data-cy="question-section-hint"
                      value={sectionHint}
                      onChange={(e) => setSectionHint(e.target.value)}
                      rows={2}
                      placeholder="If something is in progress, choose that option and give the date you expect it."
                      className="sk-input mt-1"
                    />
                    <span className="mt-1 text-[11px] text-foreground-muted">
                      Shown once, above the first question of the section.
                      Repeat the same code and title on every question that
                      belongs to it.
                    </span>
                  </label>
                </fieldset>

                {/* NO "MUST BE ANSWERED" CONTROL. It described a gate
                    that does not exist in this round: the screening
                    sitting walks every question in order, there is no
                    skip, and `required` is read nowhere in screening.py
                    or on the candidate's screen. Offering it let an
                    admin mark a question optional and be told something
                    untrue about what the candidate can do. The column
                    stays for the legacy Round 1 rows that still carry
                    it. */}

                <button
                  type="button"
                  data-cy="btn-save-question"
                  disabled={!canSave}
                  onClick={() => {
                    void save();
                  }}
                  className="sk-btn sk-btn-primary mt-6"
                >
                  {editing ? "Save changes" : "Add question"}
                </button>
              </div>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      )}
    </div>
  );
}

function QuestionRow({
  question,
  onEdit,
}: {
  question: TestQuestion;
  onEdit: (q: TestQuestion) => void;
}) {
  const { toast } = useToast();
  const patch = usePatchQuestion();

  const setActive = async (active: boolean) => {
    try {
      await patch.mutateAsync({ questionId: question.id, body: { active } });
      toast({ title: active ? "Question restored" : "Question retired" });
    } catch {
      toast({ title: "Could not change the question", variant: "error" });
    }
  };

  // CHOICE_KINDS, not `kind !== "TEXT"`. The dialog above fixed that
  // same expression when the kinds grew past three and left this copy
  // behind, so a row for an EMAIL, DATE, NUMBER or VIDEO question has
  // been rendering a trailing separator with an empty option list after
  // it. Invisible until a question with no options had something else to
  // say -- the video clock -- and then it reads as a missing value.
  const needsOptions = CHOICE_KINDS.has(question.kind);
  const isVideo = question.kind === "VIDEO";
  const kindLabel = isVideo
    ? "Spoken to camera"
    : (KINDS.find((k) => k.value === question.kind)?.label ?? question.kind);
  // The allowance is the only thing that meaningfully distinguishes two
  // spoken questions in a list, so it belongs on the row rather than
  // behind an edit click.
  const cfg = question.config_json ?? {};
  const allotted = cfg.allotted_seconds;

  return (
    <li
      data-cy="question-row"
      // NOT `sk-btn`. It was, and the class is `inline-flex` with
      // `justify-content: center` -- so every row centred its own
      // prompt, the short ones read as indented, and the long one
      // pushed the edit and retire buttons onto a line of their own. A
      // list row is not a button; it only looked like one because the
      // border and radius were roughly right.
      className="rounded-lg border border-border bg-background-card px-4 py-3"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          {/* THE TITLE LEADS: it is how a question is found in a pool of
              paragraphs. Absent on a question written before round 2. */}
          {cfg.title && (
            <p className="text-sm font-semibold text-foreground" data-cy="question-row-title">
              {cfg.title}
              {cfg.warmup && (
                <span className="ml-2 rounded-full bg-background-subtle px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-foreground-muted">
                  Warm-up · not scored
                </span>
              )}
            </p>
          )}
          <p className="text-sm text-foreground">
            {question.prompt}
            {!question.active && (
              <span
                data-cy="question-retired"
                className="ml-2 rounded-full bg-background-subtle px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-foreground-muted"
              >
                Retired
              </span>
            )}
          </p>
          <p className="mt-1 text-xs text-foreground-muted">
            {kindLabel}
            {isVideo && cfg.warmup && !cfg.title && " · Warm-up, not scored"}
            {isVideo &&
              (cfg.answer_seconds != null
                ? ` · think ${asClock(cfg.think_seconds ?? 0)}, answer ${asClock(cfg.answer_seconds)}`
                : allotted != null && ` · ${asClock(allotted)}`)}
            {needsOptions && ` · ${(question.options_json ?? []).join(" · ")}`}
          </p>
        </div>
        {/* Same bordered pills as Staff accounts. */}
        <span className="flex shrink-0 gap-2">
          <button
            type="button"
            data-cy="btn-edit-question"
            onClick={() => onEdit(question)}
            aria-label="Edit this question"
            title="Edit the wording and options"
            className="rounded-md border border-border bg-background-card p-1.5 text-foreground hover:bg-table-row-hover"
          >
            <Pencil aria-hidden className="h-4 w-4" />
          </button>
          <button
            type="button"
            data-cy="btn-retire-question"
            onClick={() => {
              void setActive(!question.active);
            }}
            aria-label={question.active ? "Retire this question" : "Restore this question"}
            title={
              question.active
                ? "Stop offering this question to new exams"
                : "Offer this question again"
            }
            className={
              question.active
                ? "rounded-md border border-status-urgent/50 bg-status-urgent-bg p-1.5 text-status-urgent hover:bg-status-urgent/20"
                : "rounded-md border border-status-wip/50 bg-status-wip-bg p-1.5 text-status-wip hover:bg-status-wip/20"
            }
          >
            {question.active ? (
              <Archive aria-hidden className="h-4 w-4" />
            ) : (
              <ArchiveRestore aria-hidden className="h-4 w-4" />
            )}
          </button>
        </span>
      </div>
    </li>
  );
}
